import { Flex, Select } from '@mantine/core';
import { IconFile, IconFolder } from '@tabler/icons-react';
import { useState } from 'react';
import { UploadedFile } from '../../hooks/file-uploads/types';
import { FileManagementModal } from '../modals/FileManagementModal';

type Props = {
  opened: boolean;
  close: () => void;
  file: UploadedFile | null;
  folders: UploadedFile[];
  rootLabel: string;
  onFileMoved: (file: UploadedFile, parentId: string | null | undefined) => Promise<void>;
};

export default function MoveFileModal({
  opened,
  close,
  file,
  folders,
  rootLabel,
  onFileMoved,
}: Props) {
  const [selectedFolderId, setSelectedFolder] = useState<string | undefined | null>(
    file?.file.parentId,
  );
  const [isMoving, setIsMoving] = useState(false);

  const rootFolder = { value: '', label: rootLabel };

  const folderOptions = [
    ...folders
      .filter(f => f.isFolder)
      .map(folder => ({
        value: folder.id,
        label: folder.file.fileName,
      })),
    rootFolder,
  ];

  const handleMove = async () => {
    if (!file) return;
    setIsMoving(true);
    try {
      await onFileMoved(file, selectedFolderId || null);
      close();
    } finally {
      setIsMoving(false);
    }
  };

  const handleSelectFolder = (folderId: string | undefined | null) => {
    setSelectedFolder(folderId);
  };

  const selectedFolder = folderOptions.find(folder => folder.value === selectedFolderId);
  const selectionValue = selectedFolder || rootFolder;

  const currentFolderName = file?.file.parentId
    ? folderOptions.find(f => f.value === file.file.parentId)?.label || 'Unknown'
    : rootLabel;

  return (
    <FileManagementModal
      opened={opened}
      onClose={close}
      title="Where would you like to move the document?"
      subtitle="Please select a folder"
      icon={<IconFile size={24} />}
      onConfirm={handleMove}
      confirmDisabled={selectedFolderId === file?.file.parentId && file?.file?.isFiled}
      confirmLoading={isMoving}
    >
      <Select
        label={currentFolderName}
        placeholder="Select folder"
        data={folderOptions}
        value={selectionValue.value}
        clearable
        onChange={handleSelectFolder}
        styles={{
          label: {
            fontWeight: 500,
          },
        }}
        leftSection={
          <Flex px="md">
            <IconFolder size={16} color="var(--mantine-color-gray-7)" />
          </Flex>
        }
      />
    </FileManagementModal>
  );
}
